import { IAdmin } from '@order-up/models';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApiService } from '../services/adminApi';
import { getAuthServiceUrl } from '../utils/env';
import { useAuth } from './useAuth';
import { createLogger } from '../utils/logger';
import { clearPersistedQueryCache } from '../utils/queryCacheStorage';
import { buildAdminLogoutUrl, type AdminSignOutOptions } from '../utils/adminLogoutUrl';
import {
    isDefinitiveNoSession,
    isSessionUnreachable,
    SessionUnreachableError,
} from '../utils/sessionOutcome';

const logger = createLogger('useAdminAuth');

/**
 * Defines the query keys used for admin authentication-related operations.
 * These are separate from admin management operations for security.
 */
export const adminAuthKeys = {
    all: ['adminAuth'] as const,
    signIn: () => [...adminAuthKeys.all, 'signIn'] as const,
    signOut: () => [...adminAuthKeys.all, 'signOut'] as const,
    session: () => [...adminAuthKeys.all, 'session'] as const,
};

/**
 * A hook to sign in as a platform admin via `POST /auth/admin/signin`.
 * Reads `admins/{uid}` on the server and returns the full IAdmin record.
 * Never touches `users/{uid}`.
 */
export const useAdminSignIn = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { admin: IAdmin; sessionToken: string; expiresAt: string; customToken?: string },
        Error,
        { idToken: string; rememberMe?: boolean }
    >({
        mutationFn: async ({ idToken, rememberMe = false }: { idToken: string; rememberMe?: boolean }) => {
            logger.debug('Starting admin sign-in mutation', { rememberMe });
            try {
                const result = await adminApiService.signInAsAdmin(idToken, rememberMe);
                logger.info('Admin sign-in completed', { uid: result.admin.uid });
                return result;
            } catch (error) {
                logger.error('Admin sign-in failed', error);
                throw error;
            }
        },
        onSuccess: (data) => {
            // Prime the session cache so AdminGuard renders immediately
            // without a second `/auth/admin/session` roundtrip.
            queryClient.setQueryData(adminAuthKeys.session(), data.admin);
        },
    });
};

/**
 * Module-level guard so rapid repeat clicks on "Log out" don't each fire a
 * fresh teardown (see the matching note on `useUserSignOut`).
 */
let adminSignOutInProgress = false;

/**
 * A hook to sign out from both server session and Firebase client SDK.
 *
 * Like `useUserSignOut`, the redirect is never gated behind a slow network
 * call: the admin per-app cookie clear and the shared server sign-out are both
 * time-boxed/best-effort, and the redirect runs in `finally` regardless. This
 * fixes the identical 1–2 minute hang the admin/billing apps shared.
 *
 * It is also the inactivity timeout's sign-out (`IdleSignOutProvider`), so an
 * idle session ends exactly as a pressed "Sign out" does: the per-app cookie,
 * the Firebase session, and `POST /auth/signout`, which clears the API cookie
 * and revokes the account's refresh tokens.
 */
export const useAdminSignOut = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { message: string },
        Error,
        AdminSignOutOptions | void
    >({
        mutationFn: async (options) => {
            if (adminSignOutInProgress) {
                logger.debug('Sign-out already in progress — ignoring repeat click');
                return { message: 'Sign-out already in progress' };
            }
            adminSignOutInProgress = true;

            const authServiceUrl = getAuthServiceUrl() || 'http://localhost:3000';
            const logoutUrl = buildAdminLogoutUrl(authServiceUrl, options);

            try {
                // Clear the admin per-app session cookie (httpOnly Next route
                // handler). Time-boxed so a slow route handler can't stall us.
                await Promise.race([
                    fetch('/api/auth/session', { method: 'DELETE' }).catch(() => {}), // wire-fold-ok: same-origin Next session route, response unread
                    new Promise<void>((resolve) => setTimeout(resolve, 2000)),
                ]);

                // Local (instant) + time-boxed best-effort server teardown.
                // adminApiService.signOut never throws.
                await adminApiService.signOut();

                try {
                    queryClient.clear();
                    // In-memory clear is not enough — the persisted slice would
                    // otherwise survive on disk into the next session.
                    clearPersistedQueryCache();
                } catch (error) {
                    logger.warn('Cache clear failed', { error: error instanceof Error ? error.message : String(error) });
                }
            } finally {
                // Redirect ALWAYS happens — and is never blocked by the network.
                logger.debug('Redirecting to auth service', { logoutUrl });
                window.location.href = logoutUrl;
            }

            return { message: 'Logged out successfully' };
        },
        onError: (error) => {
            logger.error('Signout failed', error);
        },
    });
};

/**
 * Resolves the `admin` custom claim from the current Firebase ID token.
 *
 * The claim is set by `claimsService.syncClaims` iff the user has an entry
 * in the top-level `admins/` collection — see
 * `packages/core/src/services/claimsService.ts` and the AuthClaims model.
 * Reading it client-side lets us avoid the /auth/admin/session round-trip
 * (and its 403 noise) on every cold load for users who clearly aren't
 * platform staff.
 *
 * Returns:
 *   - `true`  — claim present, user is (or recently was) a platform admin
 *   - `false` — claim absent, user is definitely not a platform admin
 *   - `undefined` — Firebase user not loaded yet, or token read failed;
 *                   callers fall through to the legacy server round-trip.
 */
const useHasAdminClaim = (): { hasClaim: boolean | undefined; shouldProbeServer: boolean } => {
    const { data: authData } = useAuth();
    const firebaseUser = authData?.firebaseUser ?? null;

    const { data, isPending } = useQuery<boolean>({
        queryKey: [...adminAuthKeys.all, 'claim', firebaseUser?.uid ?? null],
        queryFn: async () => {
            if (!firebaseUser) return false;
            try {
                const result = await firebaseUser.getIdTokenResult();
                return Boolean((result.claims as { admin?: unknown } | undefined)?.admin);
            } catch (error) {
                logger.warn('Failed to read ID token claims — falling through to server check', {
                    error: error instanceof Error ? error.message : String(error),
                });
                // Returning true biases toward the server check so we don't
                // miss a real admin because the token read transiently failed.
                return true;
            }
        },
        enabled: !!firebaseUser,
        // Claims change rarely; refetch periodically to pick up role grants
        // without forcing the user to sign back in.
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    // Whether the caller should fall through to the server round-trip.
    //
    // The gate used to be a bare `data !== false` at the consumer. That reads
    // `undefined` while the claim query is still in flight, and `undefined !==
    // false` is TRUE — so on every cold load the server probe fired BEFORE the
    // claim verdict landed and a non-admin ate a guaranteed 403. The
    // suppression only ever worked on a warm cache.
    //
    // Waiting for the verdict is correct ONLY while a Firebase user exists to
    // read a token from. With no SDK user the query is disabled and stays
    // pending forever, so a session-cookie-only admin (the TokenHandler
    // reconcile window) must still be allowed to probe — otherwise the claim
    // gate would lock out the very case it cannot answer.
    const shouldProbeServer = firebaseUser ? data !== false && !isPending : true;

    return { hasClaim: data, shouldProbeServer };
};

/**
 * Validates the current admin session against `admins/{uid}` on the server.
 * Returns the IAdmin record on success, or `null` when there's no session
 * or the user is not (or no longer) an active platform admin.
 *
 * This is the single source of truth for `AdminGuard` — there is no
 * intermediate "authenticated but not admin" state because the server
 * returns 403 in that case, which we surface as `null` here.
 *
 * Cold-path optimization: gated on the `admin` custom claim from the
 * current Firebase ID token. When the claim is explicitly absent we skip
 * the network call entirely (`data` stays `undefined` → consumers see
 * `isAdmin: false`, same outcome as the previous 403→null mapping but
 * without the wasted round-trip).
 */
export const useAdminSessionValidation = (options?: { enabled?: boolean }) => {
    const queryClient = useQueryClient();
    const { shouldProbeServer } = useHasAdminClaim();
    return useQuery<IAdmin | null, Error>({
        queryKey: adminAuthKeys.session(),
        queryFn: async () => {
            try {
                return await adminApiService.validateAdminSession();
            } catch (error: any) {
                const message: string = error?.message ?? '';

                // Ghost session: server returned 401 with the typed
                // AUTH_SESSION_INVALID code AND already cleared the cookie
                // via Set-Cookie. Wipe React Query cache so the page doesn't
                // briefly serve stale admin-scoped data before the
                // AdminGuard cascade redirects to auth.
                if (error?.code === 'AUTH_SESSION_INVALID' || message.includes('AUTH_SESSION_INVALID')) {
                    queryClient.clear();
                    return null;
                }

                // We never got a verdict. `'Network Error'` used to be mapped
                // to `null` right alongside the real 401s here, which meant an
                // unreachable API signed the admin out — the browser bounced to
                // the auth app, which found the live Firebase session and
                // bounced straight back. Surface it as an error instead so
                // AdminGuard holds rather than redirects.
                if (isSessionUnreachable(error)) {
                    logger.warn('Admin session probe could not reach the API — holding auth state', {
                        error: message,
                    });
                    throw new SessionUnreachableError(
                        'Could not reach the admin session endpoint',
                        { cause: error },
                    );
                }

                // The server answered: not an admin / no session.
                if (isDefinitiveNoSession(error) ||
                    message.includes('authorization header') ||
                    message.includes('Unauthorized') ||
                    message.includes('Forbidden')) {
                    return null;
                }
                throw error;
            }
        },
        // `shouldProbeServer` is false when the JWT explicitly lacks the admin
        // claim (→ "not an admin", no server hit needed) AND while the claim
        // read is still in flight for a known Firebase user. See the comment on
        // `useHasAdminClaim` for why the in-flight case is part of the gate.
        enabled: (options?.enabled ?? true) && shouldProbeServer,
        // A definitive verdict is returned as data (never retried). Only an
        // unreachable API is worth another go — a cold API function must not
        // read as "you are not an admin".
        retry: (failureCount, error) =>
            (error instanceof SessionUnreachableError || isSessionUnreachable(error)) &&
            failureCount < 2,
        retryDelay: (attemptIndex) => Math.min(400 * 2 ** attemptIndex, 2000),
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });
};

